// @ts-nocheck
/**
 * @module SiteMenu
 * @description Site Menu
 */
import { useEffect, useState, useContext, useRef } from "react";
import Link from "next/link";
import Image from "next/legacy/image";
import dynamic from "next/dynamic";
import { useSiteNavigation } from "~/hooks/useSiteNavigation";
import { useProfile } from "ui/hooks/useProfile";
import { stringify } from "ui/js/utils/filters";
import { fastly } from "ui/js/utils/helpers";
import { CartContext } from "ui/providers/cart";
import { trackEvent } from "ui/js/helpers/tracking";
import { addPageAction } from "ui/js/utils/new-relic";
import ComplementaryMenu from "./menus/ComplementaryMenu";
import { complementaryMenuLinks } from "./links";
import TrackElementViewed from '~/components/helpers/TrackElementViewed';

const YextSnippet = dynamic(() => import("ui/resources/Yext"));

/**
 * @function handleClick
 * @description fire off tagular events
 */
const handleClick = (event) => {
  const { target } = event;
  const payload = JSON.parse(target.getAttribute("data-payload"));
  const { text, actionOutcome, position } = payload;

  const data = {
    elementType: "BUTTON",
    location: "GLOBAL",
    position,
    text,
    actionOutcome,
  };

  trackEvent({ action: "elementClicked", data, event });
};

/**
 * @function handleBuyClick
 * @description fire New Relic cart entrance event
 */
const handleBuyClick = (event) => {
  addPageAction("cartEntrance", { method: "MASTHEAD" });
  handleClick(event);
};

/**
 * @function SiteNavigation
 * @description
 */
export const SiteNavigation = ({ ...props }) => {
  const { menus, headerLinks, userMenu, showButtons = true } = props;
  const { fetchProfile, logout } = useProfile();
  const { init } = useSiteNavigation();

  const [profile, setProfile] = useState(null);
  const [loadYext, setLoadYext] = useState(false);
  const { clickThrough } = useContext(CartContext);

  const didInitRef = useRef(false);
  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    fetchProfile()
      .then((data) => {
        setProfile(data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        // Allow time for profile menu to populate before registering event listeners
        setTimeout(() => {
          if (didInitRef.current === false) {
            didInitRef.current = true;
            init();
          }
        }, 500);
      });
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  return (
    <>
      <div className="l-container">
        <TrackElementViewed position="SECTION NAVIGATION"  triggerOnce />
        <div className="site-header-primary">
          {showButtons && (
            <div className="navigation-toggle-wrap">
              <button
                className="navigation-toggle"
                type="button"
                role="button"
                onClick={() => setLoadYext(true)}
              >
                <span className="navigation-toggle-title screen-reader-text">
                  Open Menu
                </span>
                <span className="navigation-toggle-icon">
                  <span className="icon icon-bar" />
                </span>
              </button>
              {/* <!-- .navigation-toggle--> */}
            </div>
          )}

          <div className="site-branding">
            <h1 className="site-title">
              <Link
                href="/business"
                className="site-brand"
                onClick={handleClick}
                data-payload={stringify({
                  text: "Frontier Logo",
                  position: "NAVIGATION",
                  actionOutcome: "INTERALLINK",
                })}
                rel="home"
              >
                <Image
                  src="/img/site-logo.svg"
                  alt="Frontier Communications Logo"
                  width={36}
                  height={36}
                  layout="intrinsic"
                  className="site-logo"
                  loading="eager"
                  loader={fastly}
                />
                <span className="screen-reader-text">
                  Frontier Communications
                </span>
              </Link>
            </h1>
          </div>
          {/* <!-- .site-branding --> */}

          {showButtons && (
            <div className="site-sign-in">
              {profile ? (
                <button
                  className="btn navigation-toggle--user-open"
                  type="button"
                  onClick={handleClick}
                  data-payload={stringify({
                    text: "Sign In",
                    position: "NAVIGATION",
                    actionOutcome: "INTERALLINK",
                  })}
                  role="button"
                >
                  <span className="sign-in__name">{`Hi, ${profile.firstName}`}</span>
                </button>
              ) : (
                <Link href="https://frontier.com/login">Sign In</Link>
              )}
            </div>
          )}
          {/* <!-- .site-sign-in --> */}
        </div>
        {/* <!-- .site-header-primary --> */}

        {showButtons && (
          <>
            <div className="navigation-collapse">
              <div id="navigation" className="navigation-area">
                <div className="navigation-area-search">
                  <div className="yext-search-container" data-yext-container></div>
                  {loadYext && <YextSnippet />}
                </div>
                {/* <!-- .navigation-area-search --> */}

                <div className="navigation-area-primary">
                  <nav
                    className="navigation site-navigation"
                    role="navigation"
                    aria-label="Site Navigation"
                  >
                    <ul
                      className="site-menu menu"
                      role="menu"
                      aria-label="Site Menu"
                    >
                      {menus.map((menu, index) => (
                        <li
                          key={index}
                          className={`menu-item ${
                            menu.links.length ? "menu-item-has-children" : ""
                          }`}
                          role="menuitem"
                        >
                          {menu.links.length ? (
                            <>
                              <button
                                className="btn"
                                type="button"
                                onClick={handleClick}
                                data-payload={stringify({
                                  text: menu.title,
                                  actionOutcome: "INTERALLINK",
                                })}
                                role="button"
                              >
                                {menu.title}
                              </button>
                            </>
                          ) : (
                            <>
                              <Link
                                href={menu.href}
                                target={menu.target}
                                onClick={handleClick}
                                data-payload={stringify({
                                  text: menu.title,
                                  position: "NAVIGATION",
                                  actionOutcome: "INTERALLINK",
                                })}
                              >
                                {menu.title}
                              </Link>
                            </>
                          )}

                          <div className="submenu-wrap">
                            <ul
                              className="submenu menu"
                              role="menu"
                              aria-label="Submenu"
                            >
                              {menu.links.map((link, index1) => (
                                <li key={index1} role="menuitem">
                                  <Link
                                    href={link.href}
                                    target={link.target}
                                    onClick={handleClick}
                                    data-payload={stringify({
                                      text: link.title,
                                      position: "NAVIGATION",
                                      actionOutcome: "INTERALLINK",
                                    })}
                                  >
                                    {link.title}
                                  </Link>

                                  {link.links && (
                                    <ul>
                                      {link.links.map((link1, index2) => (
                                        <li key={index2}>
                                          <Link
                                            href={link1.href}
                                            target={link1.target}
                                            onClick={handleClick}
                                            data-payload={stringify({
                                              text: link1.title,
                                              position: "NAVIGATION",
                                              actionOutcome: "INTERALLINK",
                                            })}
                                          >
                                            {link1.title}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}{" "}
                              {/* <!-- .menu-item --> */}
                            </ul>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </nav>
                  {/* <!-- .site-navigation --> */}
                </div>
                {/* <!-- .navigation-area-primary --> */}

                <div className="navigation-area-secondary">
                  <ul
                    className="menu header-menu"
                    role="menu"
                    aria-label="Header Menu"
                  >
                    {headerLinks.map((link, index) => (
                      <li key={index} role="menuitem">
                        <Link
                          href={link.href || clickThrough}
                          target={link.target}
                          onClick={link.handler}
                          data-payload={stringify({
                            text: link.title,
                            position: "NAVIGATION",
                            actionOutcome: "INTERALLINK",
                          })}
                        >
                          <span className="navigation-link-text display-none-md">
                            {link.title}
                          </span>{" "}
                          <span className={`icon ${link.icon}`} />
                        </Link>
                      </li>
                    ))}

                    <li
                      className="menu-item menu-item-has-children"
                      role="menuitem"
                    >
                      <button
                        className="btn"
                        type="button"
                        onClick={handleClick}
                        data-payload={stringify({
                          text: "Sign In",
                          position: "NAVIGATION",
                          actionOutcome: "INTERALLINK",
                        })}
                        role="button"
                      >
                        {profile ? (
                          <span className="sign-in__name">{`Hi, ${profile.firstName}`}</span>
                        ) : (
                          <>Sign In</>
                        )}
                      </button>

                      <div className="submenu-wrap">
                        <ul
                          className="submenu menu"
                          role="menu"
                          aria-label="Submenu"
                        >
                          {profile ? (
                            <>
                              {userMenu.links.map((link, index) => (
                                <li key={index} role="menuitem">
                                  <Link
                                    href={link.href}
                                    target={link.target}
                                    onClick={handleClick}
                                    data-payload={stringify({
                                      position: "USER NAVIGATION",
                                      text: link.title,
                                      actionOutcome: "INTERALLINK",
                                    })}
                                  >
                                    {link.title}
                                  </Link>
                                </li>
                              ))}
                              <li role="menuitem">
                                <button
                                  className="sign-in__logout"
                                  type="button"
                                  role="button"
                                  onClick={() => logout()}
                                >
                                  Sign Out
                                </button>
                              </li>
                            </>
                          ) : (
                            <>
                              <li role="menuitem">
                                <Link href="https://frontier.com/login">
                                  My Account
                                </Link>
                              </li>
                              <li role="menuitem">
                                <Link href="https://cobra.frontier.com/">
                                  COBRA
                                </Link>
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    </li>
                  </ul>
                </div>
                {/* <!-- .navigation-area-secondary --> */}

                <div className="navigation-area-tertiary">
                  <ComplementaryMenu links={[]} />
                </div>
                {/* <!-- .navigation-area-tertiary --> */}
              </div>
              {/* <!-- .navigation-area --> */}
            </div>
            {/* <!-- .navigation-collapse --> */}

            {profile && (
              <div className="navigation-collapse--secondary">
                <nav
                  className="navigation user-navigation"
                  role="navigation"
                  aria-label="User Navigation"
                >
                  <ul className="user-menu menu" role="menu" aria-label="User Menu">
                    <li
                      className="menu-item menu-item-has-children"
                      role="menu-item"
                    >
                      <div className="submenu-header">
                        <div className="submenu-title">My Account</div>

                        <button
                          className="navigation-toggle--user-close"
                          type="button"
                          role="button"
                        >
                          <span className="navigation-toggle-title screen-reader-text">
                            Close Menu
                          </span>
                          <span className="icon icon-close" />
                        </button>
                        {/* <!-- .navigation-toggle-close --> */}
                      </div>

                      <div className="submenu-wrap">
                        <ul
                          className="submenu menu"
                          role="menu"
                          aria-label="Submenu"
                        >
                          {userMenu.links.map((link, index) => (
                            <li key={index} role="menu-item">
                              <Link
                                href={link.href}
                                target={link.target}
                                onClick={handleClick}
                                data-payload={stringify({
                                  position: "USER NAVIGATION",
                                  text: link.title,
                                  actionOutcome: "INTERALLINK",
                                })}
                              >
                                {link.title}
                              </Link>
                            </li>
                          ))}
                          <li role="menu-item">
                            <button
                              className="sign-in__logout"
                              type="button"
                              role="button"
                              onClick={() => logout()}
                            >
                              Sign Out
                            </button>
                          </li>
                        </ul>
                      </div>
                    </li>
                  </ul>
                  {/* <!-- .user-menu --> */}
                </nav>
                {/* <!-- .user-navigation --> */}
              </div>
            )}
            {/* <!-- .navigation-collapse--secondary --> */}
          </>
        )}
      </div>
    </>
  );
};

SiteNavigation.defaultProps = {
  menus: [
    {
      title: "Offers",
      href: "/business/?section=tabItem-0",
      links: [],
    },
    {
      title: "Products",
      links: [
        { href: "/fiber-internet", title: "Frontier Fiber" },
        { href: "/unified-communications", title: "Frontier | Ring Central" },
        { href: "/fiber-voice", title: "Business Voice" },
        { href: "/select-tv-channels", title: "Business TV" },
        { href: "/secure", title: "Secure Pro" },
        { href: "/internet-backup", title: "Internet Backup" },
        { href: "/premium-tech-pro", title: "Premium Tech Pro" },
      ],
    },
    {
      title: "Support",
      links: [
        { href: "/blog", title: "Blog" },
        { href: "/testimonials", title: "Testimonials" },
        { href: "/help-center", title: "Help Center" },
        { href: "/smartvoice-help", title: "SmartVoice Help" },
        { href: "/referral-program", title: "Referral Program" },
        { href: "/local", title: "Find Your City" },
      ],
    },
  ],
  headerLinks: [
    {
      icon: "icon-magnifying-glass",
      href: "https://frontier.com/search",
      title: "Search",
      handler: handleClick,
    },
    {
      icon: "icon-cart",
      href: null,
      title: "Buy",
      handler: handleBuyClick,
    },
  ],
  userMenu: {
    links: [
      { href: "/account#/summary", title: "Account Summary" },
      { href: "/account#/payments/onetime", title: "My Payments" },
      { href: "/account#/statements/current", title: "My Bills" },
      { href: "/account#/profile", title: "My Profile" },
      {
        href: "/resources/myfrontier-mobile-app",
        title: "MyFrontier Mobile App",
        target: "_blank",
      },
    ],
  },
  complementaryLinks: [
    { href: "https://frontier.com", title: "Residential" },
    { href: "/business", title: "Small Business" },
    { href: "https://enterprise.frontier.com/", title: "Enterprise" },
    { href: "https://wholesale.frontier.com/home", title: "Wholesale" },
  ],
};

export default SiteNavigation;
